/**
 * @file notificationService.js
 * @description Notification data access layer. Handles creating, fetching, and
 *   marking notifications with payload sanitization, deduplication, and
 *   preference-gating via notificationPolicy.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/notificationService
 */
import { supabase } from "./supabase";
import { buildNotificationDedupeKey, getNotificationPolicy, isReRingableNotification, shouldDeliverNotification } from "./notificationPolicy";

export const MAX_NOTIFICATION_TITLE_LENGTH = 200;
export const MAX_NOTIFICATION_BODY_LENGTH = 2000;

// Re-export policy primitives so consumers have one import surface for
// notifications. Eliminates the need for any caller to reach into the policy
// module directly.
export { getNotificationPolicy, shouldDeliverNotification, buildNotificationDedupeKey };

/**
 * @description Normalizes a notification payload — applies the policy category,
 *   builds the dedupe key, and truncates title/body to DB limits.
 * @param {Object} [payload] - Raw notification payload
 * @returns {Object} Sanitized payload
 */
export function sanitizeNotificationPayload(payload = {}) {
  const policy = getNotificationPolicy(payload.type);
  return {
    ...payload,
    category: payload.category || policy?.category || null,
    dedupe_key: payload.dedupe_key || buildNotificationDedupeKey(payload),
    title: String(payload.title || "").trim().slice(0, MAX_NOTIFICATION_TITLE_LENGTH),
    body: payload.body == null ? null : String(payload.body).trim().slice(0, MAX_NOTIFICATION_BODY_LENGTH),
  };
}

export async function loadNotificationPrefs(userId) {
  if (!userId) return {};
  const { data, error } = await supabase
    .from("notification_preferences")
    .select("prefs")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) throw error;
  return data?.prefs || {};
}

/**
 * @description Loads the full notification_preferences row for a user
 *   (prefs + frequency + paused flag). Used by feature surfaces that show
 *   both category gates and job-alert configuration (FindCases).
 * @param {string} userId
 * @returns {Promise<{exists: boolean, frequency: string|null, prefs: Object, paused: boolean}>}
 */
export async function loadNotificationAlertConfig(userId) {
  if (!userId) return { exists: false, frequency: null, prefs: {}, paused: false };
  const { data, error } = await supabase
    .from("notification_preferences")
    .select("frequency, prefs")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) throw error;
  return {
    exists:    !!data,
    frequency: data?.frequency || null,
    prefs:     data?.prefs || {},
    paused:    data?.prefs?.paused === true,
  };
}

/**
 * @description Upserts a user's notification preferences row.
 *   Single write path for both category prefs and job-alert config.
 * @param {string} userId
 * @param {Object} payload
 * @param {Object} [payload.prefs] - Prefs object (category gates + job alert)
 * @param {string} [payload.frequency] - "immediate" | "daily" | "weekly"
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function saveNotificationPrefs(userId, { prefs, frequency } = {}) {
  if (!userId) return { ok: false, error: "missing_user_id" };
  const row = { user_id: userId, updated_at: new Date().toISOString() };
  if (prefs !== undefined)     row.prefs = prefs;
  if (frequency !== undefined) row.frequency = frequency;
  const { error } = await supabase
    .from("notification_preferences")
    .upsert(row, { onConflict: "user_id" });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export function normalizeNotification(row) {
  const ld = row.link_data || {};
  return {
    id:                 row.id,
    type:               row.type,
    title:              row.title,
    body:               row.body || "",
    time:               row.created_at,
    read:               !!row.read,
    linkView:           ld.view          || null,
    // Therapist-side deep-link (Jordan / My Applications): thread / application ID
    linkThreadId:       ld.threadId      || null,
    // Agency-side deep-link (Maria / Pipeline): applicationId + caseId for card targeting
    linkApplicationId:  ld.applicationId || null,
    linkCaseId:         ld.caseId        || null,
    // Human-friendly case code (e.g. "RBT-1042") when the notification concerns a
    // specific case — shown as a chip on the item. Present on notifications created
    // after the case-code rollout; older ones simply omit it.
    linkCaseCode:       ld.caseCode      || null,
  };
}

/**
 * @description Loads a user's notifications, newest first. NO age or plan filter
 *   here on purpose: what exists is exactly what retention kept
 *   (purge_notifications_by_plan: Pro/founder agencies full history, everyone
 *   else 30 days), so a plan gate in the client could only disagree with it.
 *   Page backwards with `before` (the created_at of the oldest row loaded).
 * @param {string} userId
 * @param {number} [limit=20]
 * @param {string|null} [before] - ISO timestamp; only rows strictly older
 * @returns {Promise<Array<Object>>}
 */
export async function fetchNotifications(userId, limit = 20, before = null) {
  let query = supabase
    .from("notifications")
    .select("*")
    .eq("user_id", userId);
  if (before) query = query.lt("created_at", before);
  const { data, error } = await query
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data || []).map(normalizeNotification);
}

export async function markNotificationRead(notificationId) {
  const { error } = await supabase
    .from("notifications")
    .update({ read: true })
    .eq("id", notificationId);

  if (error) throw error;
}

export async function markAllNotificationsRead(userId) {
  const { error } = await supabase
    .from("notifications")
    .update({ read: true })
    .eq("user_id", userId)
    .eq("read", false);

  if (error) throw error;
}

export async function markNotificationUnread(notificationId) {
  const { error } = await supabase
    .from("notifications")
    .update({ read: false })
    .eq("id", notificationId);

  if (error) throw error;
}

export async function deleteNotification(notificationId) {
  const { error } = await supabase
    .from("notifications")
    .delete()
    .eq("id", notificationId);

  if (error) throw error;
}

/**
 * @description Creates a notification row after sanitizing, gating by policy,
 *   and deduplicating.
 *
 *   Policy-driven skipPrefs: `createNotification` consults the policy registry
 *   and automatically passes `p_skip_prefs: true` to the DB when the policy
 *   marks the type as `forceDelivery: true`. Callers no longer need to know
 *   which types are force-delivery — the registry is the single source of
 *   truth. Explicitly pass `options.skipPrefs` to override.
 *
 *   Client-side preference pre-check: if `options.userPrefs` is provided, the
 *   function applies `shouldDeliverNotification` locally and returns `null`
 *   without a round-trip when the type is gated off. This is purely an
 *   optimization — the DB function applies the same gating server-side, so
 *   omitting `userPrefs` is always safe.
 *
 * @param {Object} payload - Notification payload {user_id, type, title, body, ...}
 * @param {Object} [options]
 * @param {boolean} [options.skipPrefs] - Override policy-driven prefs gating.
 *   Defaults to `policy.forceDelivery ?? false`.
 * @param {boolean} [options.skipDedupe] - Skip deduplication check
 * @param {Object} [options.userPrefs] - Pre-loaded user prefs for early bail
 * @returns {Promise<Object|null>} The created/existing row, or null if filtered
 */
export async function createNotification(payload, options = {}) {
  const safePayload = sanitizeNotificationPayload(payload);
  const policy = getNotificationPolicy(safePayload.type);

  // Policy-driven default for skipPrefs. Caller can still override either way.
  const skipPrefs = options.skipPrefs !== undefined
    ? !!options.skipPrefs
    : !!policy?.forceDelivery;

  const { skipDedupe = false, userPrefs } = options;

  // Optional client-side preference pre-check — saves a round-trip when the
  // caller already has the user's prefs loaded and the type is opt-out-able.
  if (!skipPrefs && userPrefs !== undefined && !shouldDeliverNotification(safePayload.type, userPrefs)) {
    return null;
  }

  if (skipDedupe) safePayload.dedupe_key = null;

  // Deduplication check (client-side, user reading their own rows — RLS allows this)
  if (!skipDedupe && safePayload.user_id && safePayload.dedupe_key) {
    const { data: existing, error: existingError } = await supabase
      .from("notifications")
      .select("id, read")
      .eq("user_id", safePayload.user_id)
      .eq("dedupe_key", safePayload.dedupe_key)
      .maybeSingle();

    if (existingError) throw existingError;
    // Re-ringable types (new_message: one bell per conversation): an UNREAD row
    // is still the live bell, so skip the round-trip. A READ row must fall
    // through to the RPC, which re-arms it (unread, newest content). One-shot
    // types return on any existing row, read or not.
    if (existing && !(isReRingableNotification(safePayload.type) && existing.read === true)) {
      return existing;
    }
  }

  // Use the SECURITY DEFINER RPC — bypasses RLS for cross-user inserts (e.g. admin → therapist).
  // The DB function ALSO applies preference gating, so server enforces even if
  // client misses. skipPrefs above just decides whether to ask the server to bypass.
  const { data: notifId, error } = await supabase.rpc("create_notification_for_user", {
    p_user_id:    safePayload.user_id,
    p_type:       safePayload.type,
    p_title:      safePayload.title,
    p_body:       safePayload.body ?? null,
    p_category:   safePayload.category ?? null,
    p_link_data:  safePayload.link_data ?? null,
    p_dedupe_key: safePayload.dedupe_key ?? null,
    p_skip_prefs: skipPrefs,
  });

  if (error) throw error;
  return notifId ? { id: notifId, ...safePayload } : null;
}
